import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Pricing from "@/pages/Pricing";
import BookCall from "@/pages/BookCall";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Refund from "@/pages/legal/Refund";
import Subscribe from "@/pages/Subscribe";
import Success from "@/pages/Success";
import PaymentCallback from "@/pages/PaymentCallback";
import Dashboard from "@/pages/Dashboard";
import NewAssessment from "@/pages/NewAssessment";
import AssessmentView from "@/pages/AssessmentView";
import Assessments from "@/pages/Assessments";
import Workspaces from "@/pages/Workspaces";
import WorkspaceDetail from "@/pages/WorkspaceDetail";
import WorkflowStart from "@/pages/WorkflowStart";
import WorkflowRun from "@/pages/WorkflowRun";
import Builder from "@/pages/Builder";
import Radar from "@/pages/Radar";
import Labs from "@/pages/Labs";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Settings from "@/pages/Settings";
import Methodology from "@/pages/Methodology";
import Workflows from "@/pages/Workflows";
import FinancialHealth from "@/pages/FinancialHealth";
import FinancialHealthDetail from "@/pages/FinancialHealthDetail";
import InvestmentReadiness from "@/pages/InvestmentReadiness";
import RequestAccess from "@/pages/RequestAccess";
import OnboardingStatus from "@/pages/OnboardingStatus";
import CRMPipeline from "@/pages/crm/CRMPipeline";
import LeadDetail from "@/pages/crm/LeadDetail";
import AdminDashboard from "@/pages/crm/AdminDashboard";
import InvestmentDocsList from "@/pages/investment-docs/InvestmentDocsList";
import InvestmentDocDetail from "@/pages/investment-docs/InvestmentDocDetail";
import LegalPage from "@/pages/legal/LegalPage";
import AppLayout from "@/components/AppLayout";
import LoadingScreen from "@/components/LoadingScreen";
import ErrorBoundary from "@/components/ErrorBoundary";
import UpgradeModal from "@/components/UpgradeModal";
import Billing from "@/pages/Billing";
import Welcome from "@/pages/Welcome";
import Contact from "@/pages/Contact";
import CookieConsent from "@/components/CookieConsent";
import Marketplace from "@/pages/Marketplace";
import Waitlist from "@/pages/Waitlist";
import WaitlistStatus from "@/pages/WaitlistStatus";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import RevenueReadiness from "@/pages/admin/RevenueReadiness";
import Wallet from "@/pages/Wallet";
import MissionControl from "@/pages/MissionControl";
import AssessmentExpress from "@/pages/AssessmentExpress";
import { FlagsProvider } from "@/lib/flags";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen label="Preparing your intelligence workspace" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Per-route isolation: a crash in one page must never blank the whole app.
const Guard = ({ scope, children }) => (
  <ErrorBoundary scope={scope}>{children}</ErrorBoundary>
);

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Guard scope="Landing"><Landing /></Guard>} />
      <Route path="/login" element={<Guard scope="Login"><Login /></Guard>} />
      <Route path="/signup" element={<Guard scope="Signup"><Signup /></Guard>} />
      <Route path="/pricing" element={<Guard scope="Pricing"><Pricing /></Guard>} />
      <Route path="/book" element={<Guard scope="BookCall"><BookCall /></Guard>} />
      <Route path="/contact" element={<Guard scope="Contact"><Contact /></Guard>} />
      <Route path="/legal/terms" element={<Guard scope="Terms"><Terms /></Guard>} />
      <Route path="/legal/privacy" element={<Guard scope="Privacy"><Privacy /></Guard>} />
      <Route path="/legal/refund" element={<Guard scope="Refund"><Refund /></Guard>} />
      <Route path="/assessment-express" element={<Guard scope="AssessmentExpress"><AssessmentExpress /></Guard>} />
      <Route path="/subscribe" element={<Guard scope="Subscribe"><Subscribe /></Guard>} />
      <Route path="/payment/callback" element={<Guard scope="PaymentCallback"><PaymentCallback /></Guard>} />
      <Route path="/request-access" element={<Guard scope="RequestAccess"><RequestAccess /></Guard>} />
      <Route path="/onboarding" element={<Guard scope="OnboardingStatus"><OnboardingStatus /></Guard>} />
      <Route path="/legal/:slug" element={<Guard scope="Legal"><LegalPage /></Guard>} />
      <Route path="/success" element={<Guard scope="Success"><Success /></Guard>} />
      <Route path="/welcome" element={<Guard scope="Welcome"><Welcome /></Guard>} />
      <Route path="/auth/callback" element={<Guard scope="AuthCallback"><AuthCallback /></Guard>} />
      <Route path="/methodology" element={<Guard scope="Methodology"><Methodology /></Guard>} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/early-access" element={<Waitlist />} />
      <Route path="/waitlist/status" element={<WaitlistStatus />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/dashboard" element={<Guard scope="Dashboard"><Dashboard /></Guard>} />
        <Route path="/workspaces" element={<Guard scope="Workspaces"><Workspaces /></Guard>} />
        <Route path="/workspaces/:id" element={<Guard scope="WorkspaceDetail"><WorkspaceDetail /></Guard>} />
        <Route path="/workflows" element={<Guard scope="Workflows"><Workflows /></Guard>} />
        <Route path="/workflows/:workflowId" element={<Guard scope="WorkflowStart"><WorkflowStart /></Guard>} />
        <Route path="/workflows/run/:runId" element={<Guard scope="WorkflowRun"><WorkflowRun /></Guard>} />
        <Route path="/builder" element={<Guard scope="Builder"><Builder /></Guard>} />
        <Route path="/radar" element={<Guard scope="Radar"><Radar /></Guard>} />
        <Route path="/labs" element={<Guard scope="Labs"><Labs /></Guard>} />
        <Route path="/assessments" element={<Guard scope="Assessments"><Assessments /></Guard>} />
        <Route path="/assessments/new" element={<Guard scope="NewAssessment"><NewAssessment /></Guard>} />
        <Route path="/assessments/:id" element={<Guard scope="AssessmentView"><AssessmentView /></Guard>} />
        <Route path="/knowledge" element={<Guard scope="KnowledgeBase"><KnowledgeBase /></Guard>} />
        <Route path="/financial-health" element={<Guard scope="FinancialHealth"><FinancialHealth /></Guard>} />
        <Route path="/financial-health/:id" element={<Guard scope="FinancialHealthDetail"><FinancialHealthDetail /></Guard>} />
        <Route path="/investment-readiness" element={<Guard scope="InvestmentReadiness"><InvestmentReadiness /></Guard>} />
        <Route path="/investment-docs" element={<Guard scope="InvestmentDocs"><InvestmentDocsList /></Guard>} />
        <Route path="/investment-docs/:id" element={<Guard scope="InvestmentDocDetail"><InvestmentDocDetail /></Guard>} />
        <Route path="/admin" element={<Guard scope="AdminDashboard"><AdminDashboard /></Guard>} />
        <Route path="/billing" element={<Guard scope="Billing"><Billing /></Guard>} />
        <Route path="/marketplace" element={<Guard scope="Marketplace"><Marketplace /></Guard>} />
        <Route path="/admin/waitlist" element={<Guard scope="AdminWaitlist"><AdminWaitlist /></Guard>} />
        <Route path="/admin/revenue" element={<Guard scope="RevenueReadiness"><RevenueReadiness /></Guard>} />
        <Route path="/crm" element={<Guard scope="CRMPipeline"><CRMPipeline /></Guard>} />
        <Route path="/crm/leads/:id" element={<Guard scope="LeadDetail"><LeadDetail /></Guard>} />
        <Route path="/wallet" element={<Guard scope="Wallet"><Wallet /></Guard>} />
        <Route path="/mission-control" element={<Guard scope="MissionControl"><MissionControl /></Guard>} />
        <Route path="/settings" element={<Guard scope="Settings"><Settings /></Guard>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // initial theme — LIGHT is default for premium institutional SaaS aesthetic
    const t = localStorage.getItem("orixa_theme") || "light";
    document.documentElement.classList.toggle("dark", t === "dark");
  }, []);
  return (
    <AuthProvider>
      <FlagsProvider>
        <BrowserRouter>
          <ErrorBoundary scope="App root">
            <Toaster richColors position="top-right" theme="system" />
            <UpgradeModal />
            <AppRouter />
            <CookieConsent />
          </ErrorBoundary>
        </BrowserRouter>
      </FlagsProvider>
    </AuthProvider>
  );
}

export default App;
